import type {
  ListingPaymentConfiguration,
  ListingPaymentConfigurationLoadState,
} from "@/types/listingPayment";

const CONFIGURATION_KEYS = [
  "planId",
  "currency",
  "canonicalAmount",
  "effectiveAmount",
  "environment",
  "isSandboxTestPayment",
  "configurationVersion",
  "configurationFingerprint",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>) {
  const actual = Object.keys(value).sort();
  const expected = [...CONFIGURATION_KEYS].sort();

  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}

function hasAllowedConfigurationTuple(value: Record<string, unknown>) {
  if (value.configurationVersion === 1) {
    return value.isSandboxTestPayment === true
      ? value.environment === "sandbox" && value.effectiveAmount === 1
      : value.isSandboxTestPayment === false &&
          value.effectiveAmount === value.canonicalAmount;
  }

  return (
    value.configurationVersion === 3 &&
    value.environment === "production" &&
    value.isSandboxTestPayment === false &&
    value.effectiveAmount === 1
  );
}

export function parseListingPaymentConfiguration(
  value: unknown
): ListingPaymentConfiguration {
  if (
    !isRecord(value) ||
    !hasExactKeys(value) ||
    value.planId !== "basic_business_listing_annual" ||
    value.currency !== "KES" ||
    value.canonicalAmount !== 5000 ||
    typeof value.effectiveAmount !== "number" ||
    !Number.isSafeInteger(value.effectiveAmount) ||
    value.effectiveAmount <= 0 ||
    (value.environment !== "sandbox" &&
      value.environment !== "production") ||
    typeof value.isSandboxTestPayment !== "boolean" ||
    typeof value.configurationVersion !== "number" ||
    !Number.isSafeInteger(value.configurationVersion) ||
    value.configurationVersion <= 0 ||
    typeof value.configurationFingerprint !== "string" ||
    !/^[a-f0-9]{64}$/.test(value.configurationFingerprint) ||
    !hasAllowedConfigurationTuple(value)
  ) {
    throw new Error("Invalid listing payment configuration response.");
  }

  return value as ListingPaymentConfiguration;
}

export function canInitiateWithPaymentConfiguration(
  state: ListingPaymentConfigurationLoadState
) {
  return state.status === "ready" && state.configuration !== null;
}

export function formatKesAmount(amount: number) {
  return `KSh ${amount.toLocaleString("en-KE")}`;
}
